/**
 * Module dependencies.
 */

import { Filters as AvailableFilters } from 'src/types/products';
import { DisclosureName, useFiltersDisclosures } from 'src/context/filters-disclosures/context';
import {
  availableFiltersKeys,
  availableFiltersKeysPlural,
  availableFiltersValues
} from 'src/core/collections-config/filters';

import { color, media, units } from 'src/styles/utils';
import { getKeyFromObject } from 'src/core/utils/objects';
import { useFiltersList } from 'src/context/filters-list/context';
import { useTranslate } from 'src/core/utils/translator';
import Button from 'src/components/core/buttons/button';
import FiltersSidebar from './filters-sidebar';
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import camelCase from 'lodash/camelCase';
import closeIcon from 'src/assets/svg/close.svg';
import filter from 'lodash/filter';
import filterIcon from 'src/assets/svg/filter.svg';
import get from 'lodash/get';
import head from 'lodash/head';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import size from 'lodash/size';
import split from 'lodash/split';
import styled from 'styled-components';
import useBodyScroll from 'src/hooks/use-body-scroll';
import useProductApplications from 'src/api/app/products/use-product-applications';
import useProductThicknesses from 'src/api/app/products/use-product-thicknesses';

/**
 * `Props` type.
 */

type Props = {
  availableFiltersList: AvailableFilters,
  className?: string,
  isUpdating: boolean,
  isUpdatingSucceed: boolean,
  onUpdate: () => void
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  overflow: hidden;
  position: relative;

  ${media.max('ms')`
    white-space: nowrap;

    &::after {
      background-image: linear-gradient(
        to right,
        ${color.transparentize('white', 0)},
        ${color('white')} 100%
      );
      content: '';
      height: ${units(7)};
      position: absolute;
      right: 0;
      top: 0;
      width: ${units(7)};
    }
  `}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-auto-columns: min-content;
  grid-auto-flow: column;
  grid-gap: ${units(2)};
  overflow-x: auto;
  padding-bottom: ${units(2)};
  scroll-snap-type: x mandatory;

  ${media.min('ms')`
    display: flex;
    grid-gap: 0;
    flex-flow: row wrap;
    justify-content: flex-start;
    padding-bottom: 0;

    > * {
      margin-bottom: ${units(2)};
      margin-right: ${units(2)};
    }
  `}
`;

/**
 * `useProductsFilters` hook.
 */

function useProductsFilters() {
  const { data: applications } = useProductApplications();
  const { data: thicknesses } = useProductThicknesses();
  const { translate } = useTranslate();
  const getFilterTranslation = useCallback(
    (filterType: string, value: string) => {
      const keyInTranslations = camelCase(value);

      switch (filterType) {
        case availableFiltersKeys.application:
          return get(head(filter(applications, { id: value })), 'name');

        case availableFiltersKeys.color:
          return translate(`collections:colors.${keyInTranslations}`);

        case availableFiltersKeys.effect:
          return translate(`collections:effects.${keyInTranslations}`);

        case availableFiltersKeys.finish:
          return translate(`collections:finishes.${keyInTranslations}`);

        // Formats are filtered by name, not id.
        case availableFiltersKeys.format:
          return value.replace('.', ',');

        case availableFiltersKeys.function:
          return translate(`products:functions.${keyInTranslations}`);

        case availableFiltersKeys.typology:
          return translate(`collections:typologies.${keyInTranslations}`);

        case availableFiltersKeys.thickness:
          return translate('collections:list.filterTags.thickness', {
            thickness: get(head(filter(thicknesses, { id: value })), 'name')
          });

        case availableFiltersKeys.recycleMaterial:
          return translate('collections:list.filterTags.recycleMaterial', {
            value: `${value}%`
          });

        default:
          return value;
      }
    },
    [applications, thicknesses, translate]
  );

  const existValue = useCallback(
    (filterType: string, value: any) => {
      switch (filterType) {
        case availableFiltersKeys.application:
          return size(filter(applications, { id: value })) > 0;

        case availableFiltersKeys.format:
          return !isEmpty(value);

        case availableFiltersKeys.thickness:
          return size(filter(thicknesses, { id: value })) > 0;

        case availableFiltersKeys.color:
        case availableFiltersKeys.finish:
        case availableFiltersKeys.function:
        case availableFiltersKeys.recycleMaterial:
        case availableFiltersKeys.typology:
          return Object.values(availableFiltersValues[filterType]).includes(value);

        default:
          return false;
      }
    },
    [applications, thicknesses]
  );

  return { existValue, getFilterTranslation };
}

/**
 * `Filters` component.
 */

const Filters = (props: Props): ReactElement => {
  const { availableFiltersList, className, isUpdating, onUpdate } = props;

  const { translate } = useTranslate();
  const [isOpen, setOpen] = useState<boolean>();
  const [forceUpdate, setForceUpdate] = useState<boolean>();
  const { onCloseAll, onOpenAndCloseOthers } = useFiltersDisclosures();
  const { existValue, getFilterTranslation } = useProductsFilters();
  const { onSubmitRemove, submittedFilters } = useFiltersList();
  const filterButtons = useMemo(() => {
    const keys = filter(Object.keys(availableFiltersList), (filterKey) => {
      // Recycle material filter is hidden.
      if (filterKey === 'recycleMaterial') {
        return false;
      }

      // Thickness shows with at least 1 value; other filters require more than 1.
      if (filterKey === 'thickness') {
        return size(availableFiltersList[filterKey]) > 0;
      }

      return size(availableFiltersList[filterKey]) > 1;
    });

    return map(keys, (key) => {
      const filterKey = getKeyFromObject(availableFiltersKeysPlural, key);

      return availableFiltersKeys[filterKey];
    });
  }, [availableFiltersList]);

  const handleOpenFilter = useCallback(
    (name: DisclosureName) => {
      onOpenAndCloseOthers(name);
      setOpen(true);
    },
    [onOpenAndCloseOthers]
  );

  const handleRemoveFilter = useCallback(
    (filterType, value) => {
      onSubmitRemove(filterType, value);
      setForceUpdate(true);
    },
    [onSubmitRemove]
  );

  useBodyScroll({ off: isOpen });

  useEffect(() => {
    if (!isOpen) {
      const timeout = setTimeout(() => {
        onCloseAll();
      }, 500);

      return () => clearTimeout(timeout);
    }
  }, [isOpen, onCloseAll]);

  useEffect(() => {
    if (isOpen) {
      const handleEscKey = (event: KeyboardEvent) => {
        if (event.which === 27) {
          setOpen(false);
        }
      };

      document.addEventListener('keydown', handleEscKey, false);

      return () => {
        document.removeEventListener('keydown', handleEscKey, false);
      };
    }
  }, [isOpen, setOpen]);

  useEffect(() => {
    if (forceUpdate) {
      const timeout = setTimeout(() => {
        onUpdate();
        setForceUpdate(false);
      }, 100);

      return () => clearTimeout(timeout);
    }
  }, [forceUpdate, onUpdate]);

  return (
    <>
      <Wrapper className={className}>
        <Grid>
          {!isEmpty(filterButtons) && (
            <Button icon={filterIcon} onClick={() => setOpen(true)} variant={'outlined'}>
              {translate('common:labels.filter')}
            </Button>
          )}

          {isEmpty(submittedFilters)
            ? filterButtons.map((filterKey) => {
                // Map filter keys to translation keys (some use snake_case in API but camelCase in translations)
                const translationKey = filterKey === 'recycle_material' ? 'recycleMaterial' : filterKey;

                return (
                  <Button key={filterKey} onClick={() => handleOpenFilter(filterKey)} variant={'outlined'}>
                    {translate(`common:labels.${translationKey}`)}
                  </Button>
                );
              })
            : Object.entries(submittedFilters).map(([filterType, filterValue]) =>
                split(filterValue, ',').map((value: string) => {
                  return (
                    existValue(filterType, value) && (
                      <Button
                        icon={closeIcon}
                        key={value}
                        onClick={() => handleRemoveFilter(filterType, value)}
                        variant={'outlined'}
                      >
                        {getFilterTranslation(filterType, value)}
                      </Button>
                    )
                  );
                })
              )}
        </Grid>
      </Wrapper>

      <FiltersSidebar
        availableFiltersList={availableFiltersList}
        isOpen={isOpen}
        isUpdating={isUpdating}
        onClose={() => setOpen(false)}
        onUpdate={onUpdate}
      />
    </>
  );
};

/**
 * Export `Filters` component.
 */

export default Filters;
